import { Suspense, lazy } from "react";
import { Route, Navigate } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { permissionService } from "./services/permissionService";
import { api } from "./utils/api";
import { renderWorkspaceAnalyticsRoutes } from "../admin-shared/analytics/renderAnalyticsRoutes";

const LoginSign = lazy(() => import("./pages/LoginSign"));
const KioskLandings = lazy(() => import("./pages/KioskLandings"));
const KioskLandingBuilder = lazy(() => import("./pages/KioskLandingBuilder"));
const KioskRegisterForms = lazy(() => import("./pages/KioskRegisterForms"));
const KioskRegisterFormBuilder = lazy(() => import("./pages/KioskRegisterFormBuilder"));
const SkinAnalysisSettings = lazy(() => import("./pages/SkinAnalysisSettings"));
const PageSetupWorkspace = lazy(() => import("./pages/PageSetupWorkspace"));
const FranchiseThemePacks = lazy(() => import("./pages/FranchiseThemePacks"));
const Servicelist = lazy(() => import("./pages/Servicelist"));
const ServiceManagement = lazy(() => import("./pages/ServiceManagement"));
const ServiceListReadOnly = lazy(() => import("./pages/ServiceListReadOnly"));
const WorkspaceProductCatalog = lazy(() => import("./pages/WorkspaceProductCatalog"));
const AddService = lazy(() => import("./pages/AddService"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NaturalsKpis = lazy(() => import("./pages/EnterpriseKpis"));
const Complaints = lazy(() => import("./pages/Complaints"));
const HqCheckinAnalyticsPage = lazy(() => import("../admin-shared/HqCheckinAnalyticsPage"));
const UserList = lazy(() => import("./pages/UserList"));
const AdminContactMessages = lazy(() => import("./pages/AdminContactMessages"));
const HqAdmins = lazy(() => import("./pages/HqAdmins"));
const StoreLocationRequests = lazy(() => import("./pages/StoreLocationRequests"));
const GeofenceSettings = lazy(() => import("./pages/GeofenceSettings"));
const Regions = lazy(() => import("./pages/Regions"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const Verify = lazy(() => import("./pages/Verify"));
const NaturalsOverviewPage = lazy(() => import("../admin-shared/pages/EnterpriseOverviewPage"));
const OverviewSectionHubPage = lazy(() => import("../admin-shared/pages/OverviewSectionHubPage"));
const EngagementApp = lazy(() => import("../engagement/EngagementApp"));
const WhatsAppApp = lazy(() => import("../whatsapp/WhatsAppApp"));
const LicenseManagementPage = lazy(() => import("../admin-shared/LicenseManagementPage"));
const NaturalsFlowRelease = lazy(() => import("./pages/NaturalsFlowRelease"));
const RouteFallback = () => <Loader fullScreen />;
const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const PermRoute = ({ children, check }) => {
  if (typeof check === "function" && !check()) {
    return <Navigate to="/naturals/workspace/overview" replace />;
  }
  return children;
};

export function renderWorkspaceRoutes({ workspaceHome = false } = {}) {
  return (
    <>
      {workspaceHome ? <Route path="/" element={<Navigate to="/naturals/workspace" replace />} /> : null}
      <Route path="/naturals/workspace" element={<Navigate to="/naturals/login" replace />} />
      <Route
        path="/naturals/workspace/overview"
        element={
          <Layout>
            {withSuspense(<NaturalsOverviewPage portal="workspace" permissionService={permissionService} />)}
          </Layout>
        }
      />
      <Route
        path="/naturals/workspace/overview/:sectionId"
        element={
          <Layout>
            {withSuspense(
              <OverviewSectionHubPage portal="workspace" permissionService={permissionService} />,
            )}
          </Layout>
        }
      />
      <Route
        path="/naturals/workspace/engagement/*"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/engagement/"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/engagement"
        element={<Navigate to="/naturals/workspace/engagement/" replace />}
      />
      <Route
        path="/naturals/workspace/whatsapp/*"
        element={
          <PermRoute check={() => permissionService.canAccessWhatsApp()}>
            <Layout>{withSuspense(<WhatsAppApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/whatsapp"
        element={<Navigate to="/naturals/workspace/whatsapp/accounts" replace />}
      />
      <Route
        path="/naturals/workspace/catalog-setup"
        element={<Navigate to="/naturals/workspace/overview/setup" replace />}
      />
      <Route path="/naturals/workspace/nyafaz-hub" element={<Navigate to="/naturals/workspace/overview" replace />} />
      <Route path="/naturals/activate/set-password/:token" element={withSuspense(<SetPassword />)} />
      <Route path="/naturals/activate/verify/:token" element={withSuspense(<Verify />)} />
      <Route
        path="/naturals/workspace/admin-dashboard"
        element={<Layout>{withSuspense(<AdminDashboard />)}</Layout>}
      />
      <Route
        path="/naturals/workspace/licenses"
        element={
          <PermRoute check={() => permissionService.canCreateFranchiseUsers()}>
            <Layout>
              {withSuspense(
                <LicenseManagementPage
                  api={api}
                  portalBase="/naturals/workspace"
                  portalLabel="Workspace"
                />,
              )}
            </Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/naturals-kpis"
        element={
          <PermRoute check={() => permissionService.canViewStoreKpis()}>
            <Layout>{withSuspense(<NaturalsKpis />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/complaints"
        element={
          <PermRoute check={() => permissionService.canViewComplaints()}>
            <Layout>{withSuspense(<Complaints />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/check-in-analytics"
        element={
          <PermRoute
            check={() =>
              permissionService.canViewStoreKpis() &&
              permissionService.canAccessGuestOpsAnalytics()
            }
          >
            <Layout>
              {withSuspense(
                <HqCheckinAnalyticsPage
                  api={api}
                  portalBase="/naturals/workspace"
                  portalLabel="Workspace"
                  Loader={Loader}
                />,
              )}
            </Layout>
          </PermRoute>
        }
      />
      {renderWorkspaceAnalyticsRoutes({
        withSuspense,
        Layout,
        PermRoute,
        canViewSkin: () => permissionService.canAccessSkinAnalytics(),
        canViewHair: () => permissionService.canAccessHairAnalytics(),
        canViewFlow: () =>
          permissionService.canViewStoreKpis() && permissionService.canAccessFlowAnalytics(),
        canViewGuestOps: () =>
          permissionService.canViewStoreKpis() && permissionService.canAccessGuestOpsAnalytics(),
        hqApi: api,
      })}
      <Route
        path="/naturals/workspace/kiosk-landings"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskLandings />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/kiosk-landings/:pageKey"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskLandingBuilder />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/kiosk-register-forms"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskRegisterForms />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/kiosk-register-forms/:pageKey"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskRegisterFormBuilder />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/kiosk-terms"
        element={<Navigate to="/naturals/workspace/overview" replace />}
      />
      <Route
        path="/naturals/workspace/kiosk-terms/:pageKey"
        element={<Navigate to="/naturals/workspace/overview" replace />}
      />
      <Route
        path="/naturals/workspace/page-setup"
        element={
          <PermRoute check={() => permissionService.canEditSetFlow()}>
            <Layout>{withSuspense(<PageSetupWorkspace />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/franchise-themes"
        element={
          <PermRoute
            check={() =>
              permissionService.canEditSetFlow() || permissionService.canManageKioskLandings()
            }
          >
            <Layout>{withSuspense(<FranchiseThemePacks />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/skin-analysis-settings"
        element={
          <PermRoute check={() => permissionService.canAccessSkinSettings()}>
            <Layout>{withSuspense(<SkinAnalysisSettings />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/servicelist"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<Servicelist />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/service-management"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<ServiceManagement />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/naturals-flow-release"
        element={<Layout>{withSuspense(<NaturalsFlowRelease />)}</Layout>}
      />
      <Route
        path="/naturals/workspace/service-list-readonly"
        element={
          <PermRoute check={() => permissionService.canViewOrgServicesReadonly()}>
            <Layout>{withSuspense(<ServiceListReadOnly />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/product-list"
        element={
          <PermRoute check={() => permissionService.canAccessProductCatalogAdmin()}>
            <Layout>{withSuspense(<WorkspaceProductCatalog />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/productlist"
        element={<Navigate to="/naturals/workspace/product-list" replace />}
      />
      <Route
        path="/naturals/workspace/add-service"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<AddService />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/edit-service"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<AddService />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/view-service"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<AddService />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/franchise-users"
        element={
          <PermRoute check={() => permissionService.canCreateFranchiseUsers()}>
            <Layout>{withSuspense(<UserList />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/regions"
        element={
          <PermRoute
            check={() =>
              permissionService.isSuperAdmin() || permissionService.canCreateFranchiseUsers()
            }
          >
            <Layout>{withSuspense(<Regions />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/admin-contact-messages"
        element={
          <PermRoute check={() => permissionService.canViewContactMessages()}>
            <Layout>{withSuspense(<AdminContactMessages />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/hq-admins"
        element={
          <PermRoute check={() => permissionService.canManageHqAdmins()}>
            <Layout>{withSuspense(<HqAdmins />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/store-location-requests"
        element={
          <PermRoute check={() => permissionService.canViewStoreKpis()}>
            <Layout>{withSuspense(<StoreLocationRequests />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/geofence-settings"
        element={
          <PermRoute check={() => permissionService.isAdmin()}>
            <Layout>{withSuspense(<GeofenceSettings />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/workspace/edit-profile"
        element={
          <PermRoute
            check={() =>
              permissionService.getRole() === "admin" && !permissionService.isFranchise()
            }
          >
            <Layout>{withSuspense(<EditProfile />)}</Layout>
          </PermRoute>
        }
      />
    </>
  );
}
